import React, { useState, useEffect } from "react";
import "./styles.css";
import { db, auth } from "./firebase";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  doc,
} from "firebase/firestore";
import { onAuthStateChanged, signOut } from "firebase/auth";

import Login from "./components/Login";
import TripDetail from "./components/TripDetail";

// ─── Loading Screen ───────────────────────────────────────────────
const LoadingScreen = () => (
  <div
    style={{
      height: "100vh",
      background: "#09090b",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "16px",
      fontFamily: "'Inter', sans-serif",
    }}
  >
    <span style={{ fontSize: "2.5rem" }}>🇯🇵</span>
    <div
      style={{
        width: "28px",
        height: "28px",
        border: "3px solid #27272a",
        borderTop: "3px solid #10b981",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
      }}
    />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// ─── Main App ─────────────────────────────────────────────────────
export default function App() {
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [user, setUser] = useState(null);
  const [isAuthChecking, setIsAuthChecking] = useState(true);

  const [tripName, setTripName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [editingTripId, setEditingTripId] = useState(null);
  const [isAddingTrip, setIsAddingTrip] = useState(false);

  // ─── Firebase Auth listener ───────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setIsAuthChecking(false);
    });
    return () => unsub();
  }, []);

  // ─── Theme ───────────────────────────────────────────────────
  const d = isDarkMode;
  const theme = {
    bg: d ? "#09090b" : "#f4f4f5",
    text: d ? "#fafafa" : "#09090b",
    cardBg: d ? "#18181b" : "#ffffff",
    inputBg: d ? "#09090b" : "#f4f4f5",
    border: d ? "#27272a" : "#e4e4e7",
    muted: d ? "#71717a" : "#a1a1aa",
  };

  const inputStyle = {
    padding: "9px 12px",
    borderRadius: "10px",
    border: `1px solid ${theme.border}`,
    backgroundColor: theme.inputBg,
    color: theme.text,
    fontSize: "14px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    fontFamily: "inherit",
    transition: "border-color 0.2s",
  };

  const labelStyle = {
    fontSize: "11px",
    fontWeight: "600",
    color: theme.muted,
    marginBottom: "5px",
    display: "block",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  };

  const formBgStyle = {
    backgroundColor: theme.cardBg,
    borderRadius: "14px",
    padding: "20px",
    border: `1px solid ${theme.border}`,
    boxShadow: d ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,0,0,0.06)",
  };

  // ─── Firestore ───────────────────────────────────────────────
  const fetchTrips = async () => {
    try {
      const snap = await getDocs(collection(db, "trips"));
      const loaded = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setTrips(
        loaded.sort(
          (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
        )
      );
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (user) fetchTrips();
  }, [user]);

  const handleImportTrip = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const imported = JSON.parse(event.target.result);
        const { id, ...tripData } = imported;
        await addDoc(collection(db, "trips"), {
          ...tripData,
          name: tripData.name + " (Imported)",
          createdAt: new Date(),
        });
        fetchTrips();
      } catch {
        alert("ไฟล์ไม่ถูกต้อง");
      }
    };
    reader.readAsText(file);
  };

  const resetForm = () => {
    setTripName("");
    setStartDate("");
    setEndDate("");
    setEditingTripId(null);
    setIsAddingTrip(false);
  };

  const handleCreateOrUpdateTrip = async (e) => {
    e.preventDefault();
    if (!tripName || !startDate || !endDate)
      return alert("กรุณากรอกข้อมูลให้ครบถ้วน");
    if (editingTripId) {
      await updateDoc(doc(db, "trips", editingTripId), {
        name: tripName,
        start: startDate,
        end: endDate,
      });
    } else {
      await addDoc(collection(db, "trips"), {
        name: tripName,
        start: startDate,
        end: endDate,
        itinerary: [],
        flightList: [],
        accommodations: [],
        insurances: [],
        shoppingList: [],
        createdAt: new Date(),
      });
    }
    resetForm();
    fetchTrips();
  };

  const handleEditClick = (e, trip) => {
    e.stopPropagation();
    setEditingTripId(trip.id);
    setTripName(trip.name);
    setStartDate(trip.start);
    setEndDate(trip.end);
    setIsAddingTrip(false);
  };

  const handleDelete = async (e, tripId) => {
    e.stopPropagation();
    if (window.confirm("คุณแน่ใจหรือไม่ว่าต้องการลบทริปนี้?")) {
      await deleteDoc(doc(db, "trips", tripId));
      fetchTrips();
    }
  };

  const getTripColor = (i) => {
    const c = ["#10b981", "#3b82f6", "#f43f5e", "#f59e0b", "#8b5cf6"];
    return c[i % c.length];
  };

  // ─── Night count helper ───────────────────────────────────────
  const getNights = (start, end) => {
    if (!start || !end) return 0;
    const diff = new Date(end) - new Date(start);
    return Math.max(0, Math.round(diff / 86400000));
  };

  // ─── Auth Guards ─────────────────────────────────────────────
  if (isAuthChecking) return <LoadingScreen />;
  if (!user) return <Login isDarkMode={isDarkMode} />;

  // ─── Main Render ─────────────────────────────────────────────
  return (
    <div
      style={{
        backgroundColor: theme.bg,
        color: theme.text,
        minHeight: "100vh",
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        transition: "background 0.3s",
      }}
    >
      <div
        style={{ maxWidth: "1040px", margin: "0 auto", padding: "24px 20px" }}
      >
        {!selectedTrip ? (
          <>
            {/* ─── Header ─────────────────────────────────────────── */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "32px",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              <div>
                <h1
                  style={{
                    margin: 0,
                    fontSize: "24px",
                    fontWeight: "700",
                    letterSpacing: "-0.02em",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <span style={{ fontSize: "26px" }}>🇯🇵</span> Japan Planner
                </h1>
                <p
                  style={{
                    margin: "4px 0 0",
                    fontSize: "13px",
                    color: theme.muted,
                  }}
                >
                  {user.displayName || user.email}
                </p>
              </div>

              <div
                style={{ display: "flex", gap: "8px", alignItems: "center" }}
              >
                {/* Import */}
                <label
                  style={{
                    cursor: "pointer",
                    background: d ? "#27272a" : "#fff",
                    border: `1px solid ${theme.border}`,
                    color: theme.text,
                    padding: "8px 14px",
                    borderRadius: "10px",
                    fontSize: "13px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  📂 Import
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleImportTrip}
                    style={{ display: "none" }}
                  />
                </label>

                {/* Dark/Light toggle */}
                <button
                  onClick={() => setIsDarkMode(!d)}
                  style={{
                    background: d ? "#27272a" : "#fff",
                    border: `1px solid ${theme.border}`,
                    color: theme.text,
                    borderRadius: "10px",
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: "16px",
                  }}
                >
                  {d ? "☀️" : "🌙"}
                </button>

                {/* Logout */}
                <button
                  onClick={() => signOut(auth)}
                  style={{
                    background: "rgba(239,68,68,0.08)",
                    border: "1px solid rgba(239,68,68,0.2)",
                    color: "#f87171",
                    borderRadius: "10px",
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: "16px",
                    fontWeight: "600",
                  }}
                  title="Logout"
                >
                  🚪
                </button>
              </div>
            </div>

            {/* ─── New Trip Button ──────────────────────────────────── */}
            {!isAddingTrip && !editingTripId && (
              <button
                onClick={() => setIsAddingTrip(true)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  width: "100%",
                  padding: "14px",
                  borderRadius: "14px",
                  marginBottom: "28px",
                  border: `2px dashed ${d ? "#27272a" : "#d4d4d8"}`,
                  background: "transparent",
                  color: theme.muted,
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#10b981";
                  e.currentTarget.style.color = "#10b981";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = d ? "#27272a" : "#d4d4d8";
                  e.currentTarget.style.color = theme.muted;
                }}
              >
                ＋ สร้างทริปใหม่
              </button>
            )}

            {/* ─── New Trip Form ────────────────────────────────────── */}
            {isAddingTrip && (
              <div
                style={{
                  ...formBgStyle,
                  border: `1px solid #10b981`,
                  marginBottom: "28px",
                }}
              >
                <h3
                  style={{
                    margin: "0 0 16px",
                    color: "#10b981",
                    fontSize: "15px",
                    fontWeight: "700",
                  }}
                >
                  ✈️ สร้างทริปใหม่
                </h3>
                <form onSubmit={handleCreateOrUpdateTrip}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "14px",
                    }}
                  >
                    <div>
                      <label style={labelStyle}>ชื่อทริป</label>
                      <input
                        type="text"
                        placeholder="เช่น ทริปโตเกียวหน้าหนาว"
                        value={tripName}
                        onChange={(e) => setTripName(e.target.value)}
                        style={{
                          ...inputStyle,
                          fontWeight: "600",
                          fontSize: "15px",
                        }}
                        required
                      />
                    </div>
                    <div
                      style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
                    >
                      <div style={{ flex: 1 }}>
                        <label style={labelStyle}>วันเริ่มทริป</label>
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          style={inputStyle}
                          required
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={labelStyle}>วันจบทริป</label>
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          style={inputStyle}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      marginTop: "16px",
                      justifyContent: "flex-end",
                    }}
                  >
                    <button
                      type="button"
                      onClick={resetForm}
                      style={{
                        padding: "8px 18px",
                        borderRadius: "10px",
                        border: `1px solid ${theme.border}`,
                        background: "transparent",
                        color: theme.muted,
                        cursor: "pointer",
                        fontSize: "13px",
                        fontWeight: "600",
                      }}
                    >
                      ยกเลิก
                    </button>
                    <button
                      type="submit"
                      style={{
                        padding: "8px 24px",
                        borderRadius: "10px",
                        border: "none",
                        background: "linear-gradient(135deg,#10b981,#059669)",
                        color: "#fff",
                        fontWeight: "700",
                        cursor: "pointer",
                        fontSize: "13px",
                      }}
                    >
                      สร้างทริป
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* ─── Trip List Header ─────────────────────────────────── */}
            <h2
              style={{
                margin: "0 0 16px",
                fontSize: "15px",
                fontWeight: "700",
                color: theme.muted,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              🧳 ทริปของคุณ ({trips.length})
            </h2>

            {/* ─── Trip Cards Grid ──────────────────────────────────── */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: "16px",
              }}
            >
              {trips.map((trip, index) => {
                const isEditingThis = editingTripId === trip.id;
                const cardColor = getTripColor(index);
                const nights = getNights(trip.start, trip.end);

                // ── Edit Mode ──
                if (isEditingThis) {
                  return (
                    <div
                      key={trip.id}
                      style={{
                        ...formBgStyle,
                        gridColumn: "1 / -1",
                        borderTop: `3px solid ${cardColor}`,
                      }}
                    >
                      <form onSubmit={handleCreateOrUpdateTrip}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "14px",
                          }}
                        >
                          <h4
                            style={{
                              margin: 0,
                              color: cardColor,
                              fontSize: "14px",
                              fontWeight: "700",
                            }}
                          >
                            ✏️ แก้ไขทริป
                          </h4>
                          <button
                            type="button"
                            onClick={(e) => handleDelete(e, trip.id)}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              fontSize: "18px",
                              color: "#f87171",
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "12px",
                            flexWrap: "wrap",
                          }}
                        >
                          <div style={{ flex: 2, minWidth: "160px" }}>
                            <label style={labelStyle}>ชื่อทริป</label>
                            <input
                              type="text"
                              value={tripName}
                              onChange={(e) => setTripName(e.target.value)}
                              style={{ ...inputStyle, fontWeight: "600" }}
                              required
                            />
                          </div>
                          <div style={{ flex: 1, minWidth: "120px" }}>
                            <label style={labelStyle}>วันเริ่ม</label>
                            <input
                              type="date"
                              value={startDate}
                              onChange={(e) => setStartDate(e.target.value)}
                              style={inputStyle}
                              required
                            />
                          </div>
                          <div style={{ flex: 1, minWidth: "120px" }}>
                            <label style={labelStyle}>วันจบ</label>
                            <input
                              type="date"
                              value={endDate}
                              onChange={(e) => setEndDate(e.target.value)}
                              style={inputStyle}
                              required
                            />
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            marginTop: "14px",
                            justifyContent: "flex-end",
                          }}
                        >
                          <button
                            type="button"
                            onClick={resetForm}
                            style={{
                              padding: "7px 16px",
                              borderRadius: "10px",
                              border: `1px solid ${theme.border}`,
                              background: "transparent",
                              color: theme.muted,
                              cursor: "pointer",
                              fontSize: "13px",
                              fontWeight: "600",
                            }}
                          >
                            ยกเลิก
                          </button>
                          <button
                            type="submit"
                            style={{
                              padding: "7px 22px",
                              borderRadius: "10px",
                              border: "none",
                              background: cardColor,
                              color: "#fff",
                              fontWeight: "700",
                              cursor: "pointer",
                              fontSize: "13px",
                            }}
                          >
                            บันทึก
                          </button>
                        </div>
                      </form>
                    </div>
                  );
                }

                // ── View Mode ──
                return (
                  <div
                    key={trip.id}
                    onClick={() => setSelectedTrip(trip)}
                    style={{
                      background: theme.cardBg,
                      border: `1px solid ${theme.border}`,
                      borderRadius: "16px",
                      padding: "20px",
                      cursor: "pointer",
                      position: "relative",
                      overflow: "hidden",
                      transition:
                        "transform 0.2s, box-shadow 0.2s, border-color 0.2s",
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow = d
                        ? "0 16px 40px rgba(0,0,0,0.4)"
                        : "0 16px 40px rgba(0,0,0,0.1)";
                      e.currentTarget.style.borderColor = cardColor;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                      e.currentTarget.style.borderColor = theme.border;
                    }}
                  >
                    {/* Color top bar */}
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background: `linear-gradient(90deg, ${cardColor}, ${cardColor}88)`,
                        borderRadius: "16px 16px 0 0",
                      }}
                    />

                    {/* Edit button */}
                    <button
                      onClick={(e) => handleEditClick(e, trip)}
                      style={{
                        position: "absolute",
                        top: "12px",
                        right: "12px",
                        background: d ? "#27272a" : "#f4f4f5",
                        border: `1px solid ${theme.border}`,
                        color: theme.muted,
                        borderRadius: "8px",
                        padding: "4px 9px",
                        cursor: "pointer",
                        fontSize: "13px",
                        fontWeight: "700",
                        lineHeight: 1,
                      }}
                      title="แก้ไข"
                    >
                      •••
                    </button>

                    {/* Card content */}
                    <div style={{ paddingRight: "36px", marginTop: "6px" }}>
                      <div style={{ fontSize: "26px", marginBottom: "8px" }}>
                        ✈️
                      </div>
                      <strong
                        style={{
                          fontSize: "16px",
                          color: theme.text,
                          fontWeight: "700",
                          lineHeight: "1.35",
                          letterSpacing: "-0.01em",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {trip.name}
                      </strong>
                    </div>

                    {/* Footer */}
                    <div
                      style={{
                        borderTop: `1px solid ${theme.border}`,
                        paddingTop: "12px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          color: theme.muted,
                          lineHeight: 1.6,
                        }}
                      >
                        <div>📅 {trip.start || "-"}</div>
                        <div>🏁 {trip.end || "-"}</div>
                      </div>
                      {nights > 0 && (
                        <div
                          style={{
                            background: `${cardColor}18`,
                            border: `1px solid ${cardColor}55`,
                            color: cardColor,
                            borderRadius: "999px",
                            padding: "3px 10px",
                            fontSize: "12px",
                            fontWeight: "700",
                          }}
                        >
                          {nights} คืน
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ─── Empty State ──────────────────────────────────────── */}
            {trips.length === 0 && !isAddingTrip && (
              <div
                style={{
                  textAlign: "center",
                  color: theme.muted,
                  padding: "60px 0",
                  fontSize: "14px",
                }}
              >
                <div style={{ fontSize: "3rem", marginBottom: "12px" }}>🗺️</div>
                <p style={{ margin: 0, fontWeight: "600" }}>ยังไม่มีทริป</p>
                <p style={{ margin: "4px 0 0", fontSize: "13px" }}>
                  กดปุ่มด้านบนเพื่อสร้างทริปแรกของคุณ ✈️
                </p>
              </div>
            )}
          </>
        ) : (
          <TripDetail
            trip={selectedTrip}
            onBack={() => {
              setSelectedTrip(null);
              fetchTrips();
            }}
            isDarkMode={isDarkMode}
            theme={theme}
          />
        )}
      </div>
    </div>
  );
}
